<template>
    <div class="relative w-full h-full">
        <div
            v-if="isLoading && !dataLoaded"
            class="absolute inset-0 flex-center z-10"
        >
            <Stretch :size="40" />
        </div>
        <template v-if="columns.length">
            <redesign-real-time-user-table
                v-if="isRealtimeWidget(data)"
                :columns="columns"
                :table-data="tableData"
                :columns-config="columnsConfig"
                :data="data"
                @update-widget="updateWidget"
            />
            <redesign-simple-table
                v-else-if="isSimpleTable"
                :columns="columns"
                :data="data"
                :columns-config="columnsConfig"
                :table-data="tableData"
                @update-widget="updateWidget"
            />
        </template>
        <!-- Multi queue rules template -->
        <redesign-queue-table-as-rows
            v-if="isMultiQueueRulesDashboard(data)"
            :data="data"
            :queue-table-data="queuesTableData"
            @update-widget="updateWidget"
        />
        <!-- Multi queue template (deprecated) -->
        <redesign-queue-table
            v-if="!isMultiQueueRulesDashboard(data) && isMultiQueuesDashboard(data) && isMultiQueueDeprecatedTemplate(data)"
            :data="data"
            :queue-table-data="queuesTableData"
            @update-widget="updateWidget"
        />
        <!-- Multi queue template as rows -->
        <redesign-queue-table-as-rows
            v-if="isMultiQueuesDashboard(data) && isMultiQueueAsRowsTemplate(data)"
            :data="data"
            :queue-table-data="queuesTableData"
            @update-widget="updateWidget"
        />
        <!-- Multi queue template as columns -->
        <redesign-queue-table-as-columns
            v-if="isMultiQueuesDashboard(data) && isMultiQueueAsColumnsTemplate(data)"
            :data="data"
            :queue-table-data="queuesTableData"
            @update-widget="updateWidget"
        />
        <redesign-sockets-real-time-table
            v-if="isSocketsRealTimeTableWidget(data)"
            :widget-data="data"
            @update-widget="updateWidget"
        />
        <RedesignWidgetNoData
            v-if="shouldShowWidgetNoData"
            :widget="data"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getTranslationOrUndefined } from '@/helpers/getTranslation'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import startCase from 'lodash/startCase'
import {
    isMultiQueueRulesDashboard,
    isMultiQueuesDashboard,
    isRealtimeWidget,
    isSocketsRealTimeTableWidget,
} from '@/helpers/widgetUtils.js'
import { getDynamicColumns, RealtimeDynamicColumn } from '@/enum/realtimeTableConfig'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { defaultColumnConfig } from '@/enum/redesignTableConfig'
import {
    ColumnLabel,
    WidgetExtended,
    WidgetTemplateColumnWithConfig,
    WidgetTemplateColumnWithProp
} from '@/types/dashboard'

import RedesignRealTimeUserTable from '@/components/RedesignComponents/RedesignRealTimeUserTable.vue'
import RedesignSimpleTable from '@/components/RedesignComponents/RedesignTableComponents/RedesignSimpleTable.vue'
import RedesignQueueTable from '@/components/RedesignComponents/RedesignQueueTable.vue'
import RedesignQueueTableAsRows from '@/components/RedesignComponents/RedesignQueueTableAsRows.vue'
import RedesignQueueTableAsColumns from '@/components/RedesignComponents/RedesignQueueTableAsColumns.vue'
import RedesignSocketsRealTimeTable from '@/components/RedesignComponents/RedesignSocketsRealTimeTable.vue'
import Stretch from '@/components/RedesignBaseComponents/Loader/Stretch.vue'
import RedesignWidgetNoData from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetNoData.vue'
import {
    MULTI_QUEUE_DEPRECATED_TEMPLATE_ID,
    MULTI_QUEUE_AS_ROWS_TEMPLATE_ID,
    MULTI_QUEUE_AS_COLUMNS_TEMPLATE_ID
} from '@/enum/multiQueueTemplateIds'
import TagsCachedDataService from '@/core/services/cachedData/TagsCachedDataService'
interface SimpleColumn {
    prop: string
    label?: ColumnLabel
    tooltipLabel?: string
}

type RealTimeColum = Omit<RealtimeDynamicColumn, 'label'> & {
    label?: ColumnLabel;
    tooltipLabel?: string;
};

const props = defineProps<{
    data: WidgetExtended
}>()

const emit = defineEmits<{
    (event: 'on-update', widgetData: WidgetExtended): void
}>()

const { makeTableHeaderTranslations, makeTableHeaderTooltipTranslations } = useTableHeaderTranslations()
const { data: tableData, isLoading, dataLoaded } = useWidgetDataWithRefreshInterval(props, 'data')
const columns = ref<WidgetTemplateColumnWithConfig[] | RealtimeDynamicColumn[] | SimpleColumn[]>([])

const queuesTableData = ref([])

//Computed
const columnsConfig = computed (() => {
    if (props.data && props.data.WidgetLayout.columnsObjectConfig) {
        return props.data.WidgetLayout.columnsObjectConfig
    }
    return {}
})

const isSimpleTable = computed(() => {
    return !isMultiQueueRulesDashboard(props.data) && !isMultiQueuesDashboard(props.data) && !isRealtimeWidget(props.data) && !isSocketsRealTimeTableWidget(props.data)
})

const hasNoData = computed(() => {
    if (!dataLoaded.value || isLoading.value) return false

    if (isMultiQueuesDashboard(props.data)) {
        return !queuesTableData.value?.length
    }

    return !tableData.value || (Array.isArray(tableData.value) && !tableData.value.length)
})

// Avoid duplicate empty state when a table UI is already shown.
const shouldShowWidgetNoData = computed(() =>
    hasNoData.value
    && !columns.value.length
    && !isMultiQueuesDashboard(props.data)
    && !isMultiQueueRulesDashboard(props.data)
    && !isSocketsRealTimeTableWidget(props.data)
)

// Template ID helpers for multi queue tables
function isMultiQueueDeprecatedTemplate(data: WidgetExtended) {
    return data.TemplateID === MULTI_QUEUE_DEPRECATED_TEMPLATE_ID
}

function isMultiQueueAsRowsTemplate(data: WidgetExtended) {
    return data.TemplateID === MULTI_QUEUE_AS_ROWS_TEMPLATE_ID
}

function isMultiQueueAsColumnsTemplate(data: WidgetExtended) {
    return data.TemplateID === MULTI_QUEUE_AS_COLUMNS_TEMPLATE_ID
}

// Get dynamic columns with translated labels at runtime
const dynamicColumns = computed(() => getDynamicColumns())

//Methods
const getDataAllConfig = () => {
    if (!props.data) {
        return
    }

    if (isMultiQueuesDashboard(props.data) || isMultiQueueRulesDashboard(props.data)) {
        queuesTableData.value = tableData.value
        return
    }

    const uniqueColumns: Record<string, WidgetTemplateColumnWithConfig> = {}

    if (props.data.WidgetTemplateColumns?.length) {
        props.data.WidgetTemplateColumns.forEach((el) => {

            const baseLabel = getTranslationOrUndefined(el.TemplateNameContentTag)
                || getTranslationOrUndefined(`WidgetTemplateColumnName.${el.WidgetTemplateColumnsName}`)
                || getTranslationOrUndefined(el.WidgetTemplateColumnsName)
                || startCase(el.WidgetTemplateColumnsName)

            const column : WidgetTemplateColumnWithProp = {
                ...el,
                prop: el.WidgetTemplateColumnsName,
            }

            uniqueColumns[el.WidgetTemplateColumnsName] = {
                ...column,
                label: makeTableHeaderTranslations(column, baseLabel),
                tooltipLabel: makeTableHeaderTooltipTranslations(column, baseLabel)
            }
        })
        columns.value = Object.values(uniqueColumns)
    } else if (tableData.value && Array.isArray(tableData.value) && tableData.value.length) {

        const firstRecord = tableData.value[0]
        const values = Object.keys(firstRecord)
        const total = values.length
        const count = values.filter(item => item.includes('|#|')).length

        const isCdrInfoWidget = count / total > 0.4

        const uniqueColumns: Record<string, SimpleColumn> = {}

        for (const column in firstRecord) {
            const columnData: SimpleColumn = {
                prop: column,
            }
            if(isCdrInfoWidget) {
                const [ label, tooltipLabel ] = column.split('|#|')
                columnData.label = {
                    fullTextLabel: startCase(label),
                    shortTextLabel: '',
                    iconLabel: ''
                }
                columnData.tooltipLabel = tooltipLabel

            } else {
                const baseLabel = getTranslationOrUndefined(`WidgetTemplateColumnName.${column}`)
                    || getTranslationOrUndefined(column)
                    || startCase(column)

                columnData.label = makeTableHeaderTranslations(columnData, baseLabel)
                columnData.tooltipLabel = makeTableHeaderTooltipTranslations(columnData, baseLabel)

            }

            uniqueColumns[columnData.prop] = columnData
        }
        columns.value = Object.values(uniqueColumns)
    }
    if (isRealtimeWidget(props.data)) {
        const dynamicColumnsFilteredByPresent = dynamicColumns.value.reduce<RealTimeColum[]>((acc, column) => {
            if ( !uniqueColumns[column.prop] || !uniqueColumns[column.prop.toLowerCase()]) {

                const label = makeTableHeaderTranslations(column, column.label)
                const tooltipLabel = makeTableHeaderTooltipTranslations(column, column.label)

                const newColumn: RealTimeColum = {
                    ...column,
                    label,
                    tooltipLabel
                }
                acc.push(newColumn)
            }
            return acc
        }, [])
        columns.value.splice(3, 0, ...dynamicColumnsFilteredByPresent)
    }

    const configIsAdded = Object.values(columnsConfig.value).length

    columns.value.forEach((column, index) => {
        if (configIsAdded && !columnsConfig.value[column.prop]) {
            columnsConfig.value[column.prop] = {
                ...defaultColumnConfig,
                showShortTextLabel: true,
                order: index,
            }
        }
    })
}

const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}

watch(() => tableData.value, (val) => {
    if (val) {
        getDataAllConfig()
    }
})

watch(() => TagsCachedDataService.dataRef.value, (tags) => {
    if (tags && Object.keys(tags).length && tableData.value) {
        getDataAllConfig()
    }
})
</script>

<style scoped lang="scss">

</style>
